import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useReactive } from 'ahooks';
import useDraggableScroll from 'use-draggable-scroll';

import { SettingsManager } from '@classes/settingsManager';
import RenderIf from '@common/RenderIf';
import { rootStore } from '@store/rootStore/instanse';

type TProps = {
    items: any[];
    mosesSpread?: boolean;
};

export default function AppFooter({ items, mosesSpread = false }: TProps) {
    const location = useLocation();
    const isLk =
        location.pathname.startsWith('/lk') &&
        (!location.pathname.startsWith('/lk/worker/') ||
            location.pathname === '/lk/worker/crm/create' ||
            /^\/lk\/worker\/crm\/\d+\/site$/.test(location.pathname));
    const navRef = useRef<HTMLDivElement>(null);
    const secondaryNavRef = useRef<HTMLDivElement>(null);
    const { onMouseDown } = useDraggableScroll(navRef, {
        direction: 'horizontal',
    });
    const [elementWidth, setElementWidth] = useState<string>('');
    const [scrollWidth, setScrollWidth] = useState<number>(null);
    const creds = SettingsManager.getConnectionCredentials();
    const state = useReactive({
        kbOpen: false,
    });
    const viewportBaselineRef = useRef<{ width: number; height: number } | null>(null);

    /**
     * @handleResize функция Измененте ширины элементов меню
     * navRef.current.clientWidth общая ширина текущего меню в зависимости от ширины экрана
     * 31 - сумма гэпов между элементами и паддингов
     * (navWidth -31)/7 ширина одного элемента, которую нужно отнять от общей ширины, чтобы получилось так, что последний элемент виден на половину
     *
     * kbOpen раньше сравнивался с window.screen.height (физическое разрешение экрана), из-за чего
     * ложно триггерился в DevTools device toolbar (screen не успевал синхронизироваться с эмулируемым
     * устройством) и постоянно триггерился на планшетах (там screen.height и visualViewport.height
     * расходятся из-за UI браузера сильнее чем на 200px даже без клавиатуры). Вместо этого сравниваем
     * visualViewport сам с собой: baseline сбрасывается при смене ширины (поворот экрана, ресайз
     * DevTools) или росте высоты, и клавиатурой считается только чистое уменьшение высоты без смены ширины.
     */
    function handleResize() {
        const navList = navRef.current;
        const viewport = window.visualViewport;

        if (viewport) {
            const baseline = viewportBaselineRef.current;
            const widthChanged = !baseline || Math.abs(viewport.width - baseline.width) > 1;

            if (widthChanged || viewport.height > baseline.height) {
                viewportBaselineRef.current = { width: viewport.width, height: viewport.height };
            }

            state.kbOpen = viewportBaselineRef.current.height - viewport.height > 150;
        }

        if (!navList || navList?.clientWidth <= 0) {
            return;
        }
        const elemWidth = (navList.clientWidth - (navList.clientWidth - 31) / 7) / 7;
        setElementWidth(elemWidth.toString() + 'px');
    }

    useEffect(() => {
        handleResize();
        const navList = navRef.current;
        window.addEventListener('resize', handleResize);
        window.visualViewport?.addEventListener('resize', handleResize);
        if (!navList) return;
        const dragElement = navList.getBoundingClientRect();
        if (dragElement.left < 0) {
            navList.scrollBy(-10, 0);
        }
        if (dragElement.right > window.innerWidth) {
            navList.scrollBy(10, 0);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [navRef]);

    useEffect(() => {
        const navList = navRef.current;
        if (navList === null || navList.scrollWidth <= 0) {
            return;
        }
        setScrollWidth(0);

        const timeout = setTimeout(() => {
            setScrollWidth(navList.scrollWidth - 10);
        }, 1);

        return () => clearTimeout(timeout);
    }, [items]);

    return (
        <div id="apelsin-app-footer" style={{ display: state.kbOpen ? 'none' : 'block' }}>
            <RenderIf condition={creds?.crmID != null && creds?.crmName != null}>
                <div className="footer-crm-name">{isLk ? 'Меню пользователя' : creds?.crmName}</div>
            </RenderIf>
            <div className="nav-container">
                <div ref={navRef} onMouseDown={onMouseDown} className="nav-menu">
                    <RenderIf condition={mosesSpread}>
                        <div className="cringe-meter-wrapper" style={{ width: scrollWidth }}>
                            <div
                                className={`cringe-meter${isLk ? ' cringe-meter-green' : ''}`}
                            ></div>
                        </div>
                    </RenderIf>
                    <div className={`nav-menu-wrapper${items.length == 3 ? ' center' : ''}`}>
                        {(mosesSpread
                            ? items.filter((item) => !item.key.toString().includes('personal-'))
                            : items
                        ).map((item) => {
                            return item.key == 'divider' ? (
                                <div key={item.key} className="menu-content">
                                    <div className="menu-item-divider"></div>
                                </div>
                            ) : (
                                <div
                                    key={item.key}
                                    className="menu-content"
                                    onClick={item.disabled ? null : item.onClick}
                                >
                                    <div
                                        className={
                                            `${
                                                item.disabled ? 'menu-item-disabled' : 'menu-item'
                                            }` +
                                            `${
                                                item.key.includes('personal-')
                                                    ? ' menu-item-personal'
                                                    : ''
                                            }` +
                                            `${isLk ? ' menu-item-green' : ''}` +
                                            `${
                                                rootStore.navigationStore.checkForCurrent(item.key)
                                                    ? ' menu-item-current'
                                                    : rootStore.navigationStore.ifStored(item.key)
                                                    ? ' menu-item-stored'
                                                    : ''
                                            }`
                                        }
                                        style={{ width: elementWidth }}
                                    >
                                        {item.icon}
                                    </div>
                                    <span className="menu-text">{item.title}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <RenderIf condition={mosesSpread}>
                    <div className="nav-menu-secondary">
                        <div className={`nav-menu-wrapper${items.length == 3 ? ' center' : ''}`}>
                            {items
                                .filter((item) => item.key.toString().includes('personal-'))
                                .map((item) => {
                                    return item.key == 'personal-divider' ? (
                                        <div key={item.key} className="menu-content">
                                            <div className="menu-item-divider"></div>
                                        </div>
                                    ) : (
                                        <div
                                            key={item.key}
                                            className="menu-content"
                                            onClick={item.disabled ? null : item.onClick}
                                        >
                                            <div
                                                className={`${
                                                    item.disabled
                                                        ? 'menu-item-disabled'
                                                        : 'menu-item'
                                                }${
                                                    item.key.includes('personal-')
                                                        ? ' menu-item-personal'
                                                        : ''
                                                }${
                                                    rootStore.navigationStore.checkForCurrent(
                                                        item.key
                                                    )
                                                        ? ' menu-item-current'
                                                        : ''
                                                }`}
                                                style={{ width: elementWidth }}
                                            >
                                                {item.icon}
                                            </div>
                                            <span className="menu-text">{item.title}</span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </RenderIf>
            </div>
        </div>
    );
}
